import { createFormatForDescription as cffd } from '../createFormatForDescription'
import explainAmount from '../explain/explainAmount'
import { Explanation, explain, explainOptional } from '../explain/Explanation'
import CheckCashInstructions from '../types/instructions/CheckCashInstructions'
import TransactionType from '../types/TransactionType'
import CurrencyAmount from '../types/util/CurrencyAmount'

/**
 * An explanation of a CheckCash transaction.
 *
 * @property type - The transaction type.
 * @property checkId - An explanation of the checkId.
 * @property amount - An explanation of the amount.
 * @property deliverMin - An explanation of the deliverMin.
 */
export interface CheckCashAnnotation {
  readonly type: TransactionType.CheckCash
  readonly checkId: Explanation<string>
  readonly amount?: Explanation<CurrencyAmount>
  readonly deliverMin?: Explanation<CurrencyAmount>
}

/**
 * Takes a transaction object and returns an annotated transaction object.
 * The returned object contains explanations about the various transaction features.
 *
 * @param transactionInstructions - The input transaction.
 * @returns An annotated transaction object detailing the various transaction features.
 */
export function annotate(
  transactionInstructions: CheckCashInstructions,
): CheckCashAnnotation {
  const checkId = explain(
    transactionInstructions.CheckID,
    `The check with id ${cffd(
      transactionInstructions.CheckID,
      'checkId',
    )} will be cashed.`,
  )

  const amount = explainOptional(
    transactionInstructions.Amount,
    (theAmount) => `Cash the check for exactly ${explainAmount(theAmount)}`,
  )

  const deliverMin = explainOptional(
    transactionInstructions.DeliverMin,
    (min) =>
      `Cash for the check for at least ${explainAmount(
        min,
      )} and for as much as possible.`,
  )

  return {
    checkId,
    ...(amount && { amount }),
    ...(deliverMin && { deliverMin }),

    type: TransactionType.CheckCash,
  }
}
